/**
 * ============================================================================
 * PRODUCT ADD-ONS  —  custom code, not part of the stock Warehouse theme
 * ============================================================================
 * Powers the <product-addons> element rendered by snippets/product-addons.liquid
 *
 *  - keeps a running price total as boxes are checked
 *  - reacts to variant changes ('variant:changed') and quantity changes
 *  - on Add to cart, posts ALL the line items in one /cart/add.js call
 *    (Shopify product forms can only post one variant, so we take over)
 *  - tells the theme to refresh via the documented 'product:added' event
 *
 * Checked state survives the theme's <product-rerender> swaps.
 * ============================================================================
 */
(function () {
  'use strict';

  if (window.customElements && window.customElements.get('product-addons')) return;

  /* Checked add-ons, keyed by product id, so state survives a partial rerender. */
  var CHECKED = {};

  /* --------------------------------------------------------------------- */
  /* Money formatting (mirrors Shopify's standard helper)                   */
  /* --------------------------------------------------------------------- */
  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = typeof precision === 'number' ? precision : 2;
    thousands = thousands || ',';
    decimal = decimal || '.';

    if (isNaN(number) || number === null) return '0';

    var fixed = (number / 100.0).toFixed(precision);
    var parts = fixed.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    var cents = parts[1] ? decimal + parts[1] : '';

    return dollars + cents;
  }

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');

    var value = '';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || '${{amount}}';
    var match = formatString.match(placeholder);
    var token = match ? match[1] : 'amount';

    switch (token) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_with_period_and_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholder, value);
  }

  function cartUrl(path) {
    var routes = window.routes || {};
    var base = routes[path];
    if (!base) base = path === 'cartAddUrl' ? '/cart/add' : '/cart';
    return base;
  }

  /* --------------------------------------------------------------------- */

  var ProductAddons = function () {
    return Reflect.construct(HTMLElement, [], ProductAddons);
  };

  ProductAddons.prototype = Object.create(HTMLElement.prototype);
  ProductAddons.prototype.constructor = ProductAddons;
  Object.setPrototypeOf(ProductAddons, HTMLElement);

  ProductAddons.prototype.connectedCallback = function () {
    this.productId = this.getAttribute('product-id');
    this.productTitle = this.getAttribute('product-title') || '';
    this.moneyFormat =
      this.getAttribute('money-format') ||
      (window.theme && window.theme.moneyFormat) ||
      '${{amount}}';
    this.matchQuantity = this.hasAttribute('match-quantity');
    this.showTotal = this.hasAttribute('show-total');
    this.basePrice = parseInt(this.getAttribute('base-price'), 10) || 0;

    this.form =
      document.getElementById(this.getAttribute('form-id')) || this.closest('form');

    this.checkboxes = Array.prototype.slice.call(this.querySelectorAll('[data-addon]'));
    this.totalWrapper = this.querySelector('[data-addons-total]');
    this.totalValue = this.querySelector('[data-addons-total-value]');
    this.countEl = this.querySelector('[data-addons-count]');
    this.errorEl = this.querySelector('[data-addons-error]');

    this._onChange = this.onChange.bind(this);
    this._onSubmit = this.onSubmit.bind(this);
    this._onClick = this.onClick.bind(this);
    this._onVariantChanged = this.onVariantChanged.bind(this);

    this.addEventListener('change', this._onChange);
    /* Capture on document so we run before the theme's own buy-button handler. */
    document.addEventListener('click', this._onClick, true);
    document.addEventListener('submit', this._onSubmit, true);
    document.addEventListener('variant:changed', this._onVariantChanged);

    if (this.form) {
      this._quantityInput = this.form.querySelector('[name="quantity"]');
      if (this._quantityInput) this._quantityInput.addEventListener('change', this._onChange);
    }

    this.restoreState();
    this.render();
  };

  ProductAddons.prototype.disconnectedCallback = function () {
    this.removeEventListener('change', this._onChange);
    document.removeEventListener('click', this._onClick, true);
    document.removeEventListener('submit', this._onSubmit, true);
    document.removeEventListener('variant:changed', this._onVariantChanged);
    if (this._quantityInput) this._quantityInput.removeEventListener('change', this._onChange);
  };

  /* ---- state --------------------------------------------------------- */

  ProductAddons.prototype.restoreState = function () {
    var saved = CHECKED[this.productId];
    if (!saved) return;
    this.checkboxes.forEach(function (cb) {
      if (!cb.disabled && saved.indexOf(cb.dataset.addonVariantId) !== -1) cb.checked = true;
    });
  };

  ProductAddons.prototype.saveState = function () {
    CHECKED[this.productId] = this.selected().map(function (cb) {
      return cb.dataset.addonVariantId;
    });
  };

  ProductAddons.prototype.selected = function () {
    return this.checkboxes.filter(function (cb) {
      return cb.checked && !cb.disabled;
    });
  };

  ProductAddons.prototype.quantity = function () {
    var value = this._quantityInput ? parseInt(this._quantityInput.value, 10) : 1;
    return value > 0 ? value : 1;
  };

  /* ---- rendering ----------------------------------------------------- */

  ProductAddons.prototype.onChange = function () {
    this.saveState();
    this.hideError();
    this.render();
  };

  ProductAddons.prototype.onVariantChanged = function (event) {
    var variant = event && event.detail && event.detail.variant;
    if (!variant) return;
    this.basePrice = typeof variant.price === 'number' ? variant.price : this.basePrice;
    this.render();
  };

  ProductAddons.prototype.render = function () {
    if (!this.showTotal || !this.totalWrapper) return;

    var selected = this.selected();
    var qty = this.quantity();
    var multiplier = this.matchQuantity ? qty : 1;

    var addonsTotal = selected.reduce(function (sum, cb) {
      return sum + (parseInt(cb.dataset.addonPrice, 10) || 0) * multiplier;
    }, 0);

    var total = this.basePrice * qty + addonsTotal;

    this.totalWrapper.hidden = selected.length === 0;
    if (this.totalValue) this.totalValue.textContent = formatMoney(total, this.moneyFormat);
    if (this.countEl) {
      this.countEl.textContent =
        selected.length === 1 ? '(1 add-on)' : '(' + selected.length + ' add-ons)';
    }
  };

  /* ---- add to cart --------------------------------------------------- */

  ProductAddons.prototype.ownsEvent = function (target) {
    return !!(this.form && target && this.form.contains(target));
  };

  ProductAddons.prototype.onClick = function (event) {
    if (this.selected().length === 0) return;

    var button = event.target.closest
      ? event.target.closest('button[type="submit"], input[type="submit"]')
      : null;
    if (!button || !this.ownsEvent(button)) return;

    event.preventDefault();
    event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();
    this.addToCart(button);
  };

  ProductAddons.prototype.onSubmit = function (event) {
    if (this.selected().length === 0) return;
    if (event.target !== this.form) return;

    event.preventDefault();
    event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();
    this.addToCart(this.form.querySelector('[type="submit"]'));
  };

  ProductAddons.prototype.parentProperties = function () {
    var props = {};
    if (!this.form) return props;

    Array.prototype.forEach.call(
      this.form.querySelectorAll('[name^="properties["]'),
      function (el) {
        if (el.disabled) return;
        if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
        if (!el.value) return;
        var key = el.name.slice('properties['.length, -1);
        props[key] = el.value;
      }
    );

    return props;
  };

  ProductAddons.prototype.addToCart = function (button) {
    var self = this;
    if (this._busy || !this.form) return;

    var idInput = this.form.querySelector('[name="id"]');
    if (!idInput || !idInput.value) return;

    var qty = this.quantity();
    var multiplier = this.matchQuantity ? qty : 1;

    var items = [
      {
        id: Number(idInput.value),
        quantity: qty,
        properties: this.parentProperties()
      }
    ];

    var sellingPlan = this.form.querySelector('[name="selling_plan"]');
    if (sellingPlan && sellingPlan.value) items[0].selling_plan = Number(sellingPlan.value);

    this.selected().forEach(function (cb) {
      items.push({
        id: Number(cb.dataset.addonVariantId),
        quantity: multiplier,
        /* Leading underscore hides the property in cart + mini-cart. */
        properties: { _Add_on_for: self.productTitle }
      });
    });

    this.setBusy(button, true);
    this.hideError();

    fetch(cartUrl('cartAddUrl') + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok, data: data };
        });
      })
      .then(function (result) {
        if (!result.ok) {
          throw new Error(result.data.description || result.data.message || '');
        }

        var addedQuantity = items.reduce(function (sum, item) {
          return sum + item.quantity;
        }, 0);

        /* Cart page mode: no mini-cart to refresh, just go there. */
        if (window.theme && window.theme.cartType === 'page') {
          window.location.href = cartUrl('cartUrl');
          return;
        }

        /* Documented Warehouse hook: re-renders and surfaces the mini-cart. */
        document.documentElement.dispatchEvent(
          new CustomEvent('product:added', {
            bubbles: true,
            detail: {
              quantity: addedQuantity,
              variant: result.data.items ? result.data.items[0] : undefined
            }
          })
        );

        /* Belt and suspenders: make sure the mini-cart contents are in sync. */
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:refresh', { bubbles: true, detail: { scrollToTop: false } })
        );
      })
      .catch(function (error) {
        self.showError(error && error.message ? error.message : '');
      })
      .finally(function () {
        self.setBusy(button, false);
      });
  };

  ProductAddons.prototype.setBusy = function (button, busy) {
    this._busy = busy;
    if (!button) return;
    button.disabled = busy;
    button.setAttribute('aria-busy', busy ? 'true' : 'false');
    button.classList.toggle('is-loading', busy);
  };

  ProductAddons.prototype.showError = function (message) {
    if (!this.errorEl) return;
    this.errorEl.textContent =
      message || this.getAttribute('error-text') || 'Sorry, something went wrong.';
    this.errorEl.hidden = false;
  };

  ProductAddons.prototype.hideError = function () {
    if (this.errorEl) this.errorEl.hidden = true;
  };

  window.customElements.define('product-addons', ProductAddons);
})();
